import React, { useState, useRef, useEffect, useCallback } from "react"
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import MicIcon from "@mui/icons-material/Mic"
import StopIcon from "@mui/icons-material/Stop"
import { toast } from "react-toastify"
import { CopilotVoiceIcon, CopilotBoxIcon } from "../../Icons/LivisIcons"
import { useTranslation } from "react-i18next"

export const MAX_QUERY_LENGTH = 2000

export type MentionTarget = {
  type:
    | "station"
    | "inspector"
    | "part"
    | "training"
    | "report"
    | "augmentation"
    | "synthetic"
    | "dataset"
  id: string
  label: string
  workstation_id?: string
  station_id?: string
  usecase_id?: string
  ai_inspector_id?: string
  training_id?: string
  experiment_id?: string
}

const MENTION_LIMIT = 14

function mentionTrigger(
  value: string,
  selectedMentions: MentionTarget[] = []
): { start: number; query: string } | null {
  const match = /(^|\s)@([^@]*)$/.exec(value)
  if (!match) return null
  const rawQuery = match[2] || ""
  const selected = selectedMentions.some((mention) => {
    const label = mention.label.trim()
    return rawQuery === label || rawQuery.startsWith(`${label} `)
  })
  if (selected) return null
  return { start: match.index + match[1].length, query: rawQuery.trim() }
}

function mentionTypeLabel(type: MentionTarget["type"]): string {
  if (type === "station") return "Station"
  if (type === "inspector") return "AI Inspector"
  if (type === "part") return "Part"
  if (type === "training") return "Training"
  if (type === "report") return "Report"
  if (type === "augmentation") return "Augmentation"
  if (type === "synthetic") return "Synthetic"
  return "Dataset"
}

function mentionTypeInitial(type: MentionTarget["type"]): string {
  if (type === "station") return "S"
  if (type === "inspector") return "AI"
  if (type === "part") return "P"
  if (type === "training") return "T"
  if (type === "report") return "R"
  if (type === "augmentation") return "A"
  if (type === "synthetic") return "SY"
  return "D"
}

function mentionTypeColor(type: MentionTarget["type"]): {
  bg: string
  fg: string
} {
  if (type === "station")
    return {
      bg: "var(--livis-hover-color)",
      fg: "var(--livis-color-primary-blue)",
    }
  if (type === "inspector")
    return {
      bg: "var(--livis-light-success-green-bg-color)",
      fg: "var(--livis-success-color)",
    }
  if (type === "part")
    return {
      bg: "var(--livis-experiment-box-bg-color)",
      fg: "var(--livis-warning-color)",
    }
  if (type === "training")
    return {
      bg: "var(--livis-pale-lavender-half-transparent-bg-color)",
      fg: "var(--livis-deep-purple-accent-color)",
    }
  if (type === "report")
    return {
      bg: "var(--livis-deep-blue-ultralight-bg-color)",
      fg: "var(--livis-color-primary-blue)",
    }
  if (type === "augmentation")
    return {
      bg: "var(--livis-light-yellow-bg-color)",
      fg: "var(--livis-warning-color)",
    }
  if (type === "synthetic")
    return {
      bg: "var(--livis-light-success-green-bg-color)",
      fg: "var(--livis-success-color)",
    }
  return {
    bg: "var(--livis-transparent-black-light-bg-color)",
    fg: "var(--chat-text-1)",
  }
}

const MENTION_TYPE_ORDER: MentionTarget["type"][] = [
  "station",
  "inspector",
  "part",
  "training",
  "report",
  "augmentation",
  "synthetic",
  "dataset",
]

// Owns inputValue so that every keystroke only re-renders this tiny component,
// not the entire AIChatWindow (which contains the heavy memoized message list).

/** One node in the agent's active context breadcrumb. */
export interface ContextCrumb {
  type: "station" | "inspector" | "part" | "training" | "screen"
  label: string
}

/**
 * Full agent context shown in the banner above the input.
 * crumbs = entity chain or screen topic (e.g. [Station, Inspector, Part]).
 * clearable = true when the X button should be shown (entity binding exists).
 */
export interface ActiveContext {
  crumbs: ContextCrumb[]
  clearable: boolean
}

interface ChatInputAreaProps {
  isLoading: boolean
  onSend: (text: string, mentions?: MentionTarget[]) => void
  onCancel: () => void
  inputMode: "agent" | "ask"
  onModeChange: (mode: "agent" | "ask") => void
  onMentionSearch?: (query: string) => Promise<MentionTarget[]>
  onMentionsChange?: (mentions: MentionTarget[]) => void
  activeContext?: ActiveContext | null
  onClearContext?: () => void
  isDisconnected?: boolean
}

function crumbColor(type: ContextCrumb["type"]): { bg: string; fg: string } {
  if (type === "station")
    return {
      bg: "var(--livis-hover-color)",
      fg: "var(--livis-color-primary-blue)",
    }
  if (type === "inspector")
    return {
      bg: "var(--livis-light-success-green-bg-color)",
      fg: "var(--livis-success-color)",
    }
  if (type === "part")
    return {
      bg: "var(--livis-experiment-box-bg-color)",
      fg: "var(--livis-warning-color)",
    }
  if (type === "training")
    return {
      bg: "var(--livis-pale-lavender-half-transparent-bg-color)",
      fg: "var(--livis-deep-purple-accent-color)",
    }
  // screen / topic
  return {
    bg: "var(--livis-deep-blue-ultralight-bg-color)",
    fg: "var(--livis-color-primary-blue)",
  }
}

function crumbInitial(type: ContextCrumb["type"]): string {
  if (type === "station") return "S"
  if (type === "inspector") return "I"
  if (type === "part") return "P"
  if (type === "training") return "T"
  return "•"
}

export const ChatInputArea = React.memo(function ChatInputArea({
  isLoading,
  onSend,
  onCancel,
  inputMode,
  onModeChange,
  onMentionSearch,
  onMentionsChange,
  activeContext,
  onClearContext,
  isDisconnected,
}: ChatInputAreaProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState("")
  const [isListening, setIsListening] = useState(false)
  const [modeAnchorEl, setModeAnchorEl] = useState<null | HTMLElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [mentionOptions, setMentionOptions] = useState<MentionTarget[]>([])
  const [selectedMentions, setSelectedMentions] = useState<MentionTarget[]>([])
  const [mentionLoading, setMentionLoading] = useState(false)
  const recognitionRef = useRef<any>(null)
  const onMentionsChangeRef = useRef(onMentionsChange)
  useEffect(() => {
    onMentionsChangeRef.current = onMentionsChange
  }, [onMentionsChange])
  useEffect(() => {
    onMentionsChangeRef.current?.(selectedMentions)
  }, [selectedMentions])

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop()
    }
  }, [])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto"
      const scrollHeight = inputRef.current.scrollHeight
      inputRef.current.style.height = `${Math.min(scrollHeight, 200)}px`
    }
  }, [inputValue])

  useEffect(() => {
    const trigger = mentionTrigger(inputValue, selectedMentions)
    if (!trigger) {
      setMentionOptions([])
      setMentionLoading(false)
      return
    }
    let cancelled = false
    setMentionLoading(true)
    const timer = window.setTimeout(() => {
      ;(onMentionSearch ? onMentionSearch(trigger.query) : Promise.resolve([]))
        .then((items) => {
          if (!cancelled)
            setMentionOptions((items || []).slice(0, MENTION_LIMIT))
        })
        .catch(() => {
          if (!cancelled) setMentionOptions([])
        })
        .finally(() => {
          if (!cancelled) setMentionLoading(false)
        })
    }, 180)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [inputValue, onMentionSearch, selectedMentions])

  const selectMention = useCallback(
    (target: MentionTarget) => {
      const trigger = mentionTrigger(inputValue, selectedMentions)
      const nextText = trigger
        ? `${inputValue.slice(0, trigger.start)}@${target.label} `
        : `${inputValue} @${target.label} `
      setInputValue(nextText)
      setSelectedMentions((prev) => {
        const without = prev.filter(
          (m) => !(m.type === target.type && m.id === target.id)
        )
        return [...without, target].slice(0, 8)
      })
      setMentionOptions([])
      requestAnimationFrame(() => inputRef.current?.focus())
    },
    [inputValue, selectedMentions]
  )

  const handleSend = useCallback(() => {
    const text = inputValue.trim()
    if (!text || inputValue.length > MAX_QUERY_LENGTH) return
    const mentions = selectedMentions.slice(0, 8)
    setInputValue("")
    setSelectedMentions([])
    setMentionOptions([])
    onSend(text, mentions)
    inputRef.current?.focus()
  }, [inputValue, selectedMentions, onSend])

  const activeMentionTrigger = mentionTrigger(inputValue, selectedMentions)
  const mentionOpen = Boolean(activeMentionTrigger)
  const groupedMentionOptions = MENTION_TYPE_ORDER.map((type) => ({
    type,
    items: mentionOptions.filter((item) => item.type === type),
  })).filter((group) => group.items.length > 0)

  const startVoice = useCallback(() => {
    if (isLoading) return

    if (isListening) {
      recognitionRef.current?.stop()
      setIsListening(false)
      return
    }

    const SR =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition
    if (!SR) {
      toast.error(t("Voice recognition is not supported in this browser."))
      return
    }
    recognitionRef.current?.stop()
    const rec = new SR()
    recognitionRef.current = rec
    rec.lang = "en-US"
    rec.continuous = false
    rec.interimResults = false
    rec.start()
    setIsListening(true)
    rec.onresult = (ev: any) => {
      const transcript = ev?.results?.[0]?.[0]?.transcript || ""
      if (transcript)
        setInputValue((prev) => (prev ? `${prev} ${transcript}` : transcript))
      setIsListening(false)
    }
    rec.onerror = (ev: any) => {
      setIsListening(false)
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        toast.error(
          t(
            "Microphone access denied. Please allow microphone permission in your browser."
          )
        )
      } else if (ev.error === "network") {
        toast.error(
          t(
            "Voice recognition failed. Ensure the page is served over HTTPS — Chrome requires a secure connection for speech recognition."
          )
        )
      } else if (ev.error === "no-speech") {
        toast.error(t("No speech detected. Please try again."))
      } else {
        toast.error(`Voice recognition error: ${ev.error || "unknown"}`)
      }
    }
    rec.onend = () => setIsListening(false)
  }, [isLoading, isListening])

  const handleModeSelect = (mode: "agent" | "ask") => {
    if (mode === "ask") {
      // Clear mentions — agent-only entity binding, not valid in ask mode.
      setSelectedMentions([])
      setMentionOptions([])
      // Strip @mention text from input
      setInputValue((v) =>
        v
          .replace(/@\S+/g, "")
          .replace(/\s{2,}/g, " ")
          .trim()
      )
    }
    onModeChange(mode)
    setModeAnchorEl(null)
  }

  return (
    <Box className="livis-ai-assistant-input-wrapper">
      {activeContext && activeContext.crumbs.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.6,
            mb: 0.5,
            borderRadius: "10px",
            bgcolor: "var(--chat-surface, var(--livis-white-color))",
            border: "1px solid var(--chat-border, var(--livis-border-color))",
            fontSize: 12,
            color: "var(--chat-text-2, var(--livis-gray-font-color))",
            minHeight: 32,
          }}
        >
          {/* Context pin icon */}
          <Box
            component="span"
            sx={{ fontSize: 12, flexShrink: 0, opacity: 0.55, lineHeight: 1 }}
          >
            ⊕
          </Box>

          <Box
            component="span"
            sx={{ fontWeight: 600, flexShrink: 0, fontSize: 11, opacity: 0.7 }}
          >
            Context:
          </Box>

          {/* Breadcrumb crumbs */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexWrap: "nowrap",
              overflow: "hidden",
              flex: 1,
            }}
          >
            {activeContext.crumbs.map((crumb, idx) => {
              const colors = crumbColor(crumb.type)
              return (
                <React.Fragment key={`${crumb.type}:${crumb.label}`}>
                  {idx > 0 && (
                    <Box
                      component="span"
                      sx={{ fontSize: 10, opacity: 0.4, flexShrink: 0 }}
                    >
                      ›
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.4,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: "6px",
                      bgcolor: colors.bg,
                      color: colors.fg,
                      fontWeight: 700,
                      fontSize: 11,
                      flexShrink:
                        idx === activeContext.crumbs.length - 1 ? 1 : 0,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: 9,
                        fontWeight: 800,
                        flexShrink: 0,
                        opacity: 0.8,
                      }}
                    >
                      {crumbInitial(crumb.type)}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {crumb.label}
                    </Box>
                  </Box>
                </React.Fragment>
              )
            })}
          </Box>

          {/* Clear button — only when entity is explicitly bound */}
          {activeContext.clearable && (
            <Tooltip title={t("Clear context") as string}>
              <IconButton
                size="small"
                onClick={onClearContext}
                sx={{
                  p: 0.25,
                  flexShrink: 0,
                  color: "var(--chat-text-2)",
                  "&:hover": { color: "var(--chat-text-1)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <Box
        className="livis-ai-assistant-input-container"
        sx={{ position: "relative", overflow: "visible" }}
      >
        <textarea
          ref={inputRef}
          placeholder={
            isDisconnected
              ? (t("Service not available") as string)
              : isLoading
              ? (t("Send to interrupt and ask something new…") as string)
              : (t(
                  "Ask LIVIS Copilot or mention @workstation, @AI inspector, @part..."
                ) as string)
          }
          value={inputValue}
          rows={1}
          disabled={false}
          maxLength={MAX_QUERY_LENGTH + 200}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              if (inputValue.length <= MAX_QUERY_LENGTH) handleSend()
            }
          }}
          onChange={(e) => setInputValue(e.target.value)}
        />

        {selectedMentions.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              px: 0.5,
              pt: 0.75,
            }}
          >
            {selectedMentions.map((mention) => {
              const colors = mentionTypeColor(mention.type)
              return (
                <Box
                  key={`${mention.type}:${mention.id}`}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.6,
                    maxWidth: "100%",
                    px: 0.9,
                    py: 0.45,
                    borderRadius: "8px",
                    bgcolor: colors.bg,
                    color: colors.fg,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontSize: 10, fontWeight: 800, flexShrink: 0 }}
                  >
                    {mentionTypeInitial(mention.type)}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {mention.label}
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}

        {mentionOpen && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "calc(100% + 10px)",
              zIndex: 999999,
              bgcolor: "var(--chat-bg-1, var(--livis-white-color))",
              border:
                "1px solid var(--chat-border, var(--livis-transparent-black-light-bg-color))",
              borderRadius: "12px",

              overflow: "hidden",
              maxHeight: 340,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 0.9,
                borderBottom:
                  "1px solid var(--livis-transparent-black-light-bg-color)",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "6px",
                  bgcolor: "var(--livis-deep-blue-ultralight-bg-color)",
                  color: "var(--chat-accent, var(--livis-color-primary-blue))",
                  fontSize: 14,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                @
              </Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--chat-text-1)",
                }}
              >
                LIVIS context
              </Typography>
              {activeMentionTrigger?.query ? (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "var(--chat-text-2)",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeMentionTrigger.query}
                </Typography>
              ) : null}
            </Box>

            <Box sx={{ overflowY: "auto", maxHeight: 286, py: 0.5 }}>
              {mentionLoading && !mentionOptions.length ? (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    color: "var(--chat-text-2)",
                    fontSize: 13,
                  }}
                >
                  Searching...
                </Box>
              ) : groupedMentionOptions.length ? (
                groupedMentionOptions.map((group) => (
                  <Box key={group.type} sx={{ py: 0.35 }}>
                    <Typography
                      sx={{
                        px: 1.5,
                        py: 0.45,
                        fontSize: 11,
                        fontWeight: 800,
                        color: "var(--chat-text-2)",
                        textTransform: "uppercase",
                        letterSpacing: 0,
                      }}
                    >
                      {mentionTypeLabel(group.type)}
                    </Typography>
                    {group.items.map((item) => {
                      const colors = mentionTypeColor(item.type)
                      return (
                        <Box
                          key={`${item.type}:${item.id}`}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            selectMention(item)
                          }}
                          sx={{
                            mx: 0.5,
                            px: 1,
                            py: 0.9,
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "grid",
                            gridTemplateColumns: "30px minmax(0, 1fr) auto",
                            alignItems: "center",
                            gap: 1,
                            "&:hover": {
                              bgcolor:
                                "var(--livis-deep-blue-ultralight-bg-color)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "7px",
                              bgcolor: colors.bg,
                              color: colors.fg,
                              fontSize: 11,
                              fontWeight: 800,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {mentionTypeInitial(item.type)}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 650,
                              color: "var(--chat-text-1)",
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: "var(--chat-text-2)",
                              flexShrink: 0,
                            }}
                          >
                            {mentionTypeLabel(item.type)}
                          </Typography>
                        </Box>
                      )
                    })}
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    color: "var(--chat-text-2)",
                    fontSize: 13,
                  }}
                >
                  No matching LIVIS context
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box className="livis-ai-input-actions-row">
          <Box className="livis-ai-input-left">
            <Box
              className="livis-ai-agent-dropdown-btn"
              onClick={(e) => setModeAnchorEl(e.currentTarget)}
            >
              <CopilotBoxIcon
                style={{ width: 16, height: 16, marginRight: 4 }}
              />
              <Box
                component="span"
                sx={{ fontSize: "0.85rem", fontWeight: 600, lineHeight: 1 }}
              >
                {inputMode === "agent" ? "LIVIS Agent" : "LIVIS Ask"}
              </Box>
              <KeyboardArrowDownIcon sx={{ fontSize: 16, ml: 0.5 }} />
            </Box>
            <Menu
              anchorEl={modeAnchorEl}
              open={Boolean(modeAnchorEl)}
              onClose={() => setModeAnchorEl(null)}
              sx={{ zIndex: 9999999 }}
              PaperProps={{
                elevation: 0,
                sx: {
                  borderRadius: "0.75rem",
                  mt: -1,
                  transform: "translateY(-100%)",
                  minWidth: 140,
                  maxWidth: 280,
                  border: "1px solid var(--chat-border)",
                  bgcolor: "var(--livis-white-color) !important",
                  boxShadow: "none",
                },
              }}
              transformOrigin={{ horizontal: "left", vertical: "bottom" }}
              anchorOrigin={{ horizontal: "left", vertical: "top" }}
              MenuListProps={{
                sx: { bgcolor: "transparent !important" },
              }}
            >
              <MenuItem
                onClick={() => handleModeSelect("agent")}
                selected={inputMode === "agent"}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  mb: 0.5,
                  p: 1,
                  backgroundColor: "transparent !important",
                  "&.Mui-selected": {
                    backgroundColor: "transparent !important",
                  },
                  "&:hover": {
                    backgroundColor:
                      "var(--livis-search-background-color) !important",
                  },
                }}
              >
                <Typography sx={{ fontSize: "0.85rem", mb: 0.25 }}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    LIVIS
                  </Box>{" "}
                  {t("Agent")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    mt: 0.5,
                    color: "var(--livis-gray-font-color) !important",
                    whiteSpace: "normal",
                    lineHeight: 1.3,
                  }}
                >
                  {t("Completes tasks on the platform for precise results without navigation.")}
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() =>
                  selectedMentions.length === 0 && handleModeSelect("ask")
                }
                selected={inputMode === "ask"}
                disabled={selectedMentions.length > 0}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  p: 1,
                  backgroundColor: "transparent !important",
                  "&.Mui-selected": {
                    backgroundColor: "transparent !important",
                  },
                  "&:hover": {
                    backgroundColor:
                      "var(--livis-search-background-color) !important",
                  },
                }}
              >
                <Typography sx={{ fontSize: "0.85rem", mb: 0.25 }}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    LIVIS
                  </Box>{" "}
                  {t("Ask")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    mt: 0.5,
                    color: "var(--livis-gray-font-color) !important",
                    whiteSpace: "normal",
                    lineHeight: 1.3,
                  }}
                >
                  {t("Answers all platform questions from station setup to analytics.")}
                </Typography>
              </MenuItem>
            </Menu>
          </Box>

          <Box className="livis-ai-input-right">
            <IconButton
              className={`livis-ai-icon-btn ${isListening ? "listening" : ""}`}
              size="small"
              onClick={startVoice}
              disabled={isLoading || isDisconnected}
              sx={{ color: "var(--chat-text-2)", mr: 0.5 }}
            >
              <CopilotVoiceIcon style={{ width: 20, height: 20 }} />
            </IconButton>
            {isLoading && !isDisconnected ? (
              <Box className="livis-ai-send-btn stop" onClick={onCancel}>
                <StopIcon sx={{ fontSize: 18 }} />
              </Box>
            ) : (
              <Box
                className={`livis-ai-send-btn ${
                  !inputValue.trim() || inputValue.length > MAX_QUERY_LENGTH
                    ? "disabled"
                    : ""
                }`}
                onClick={
                  !inputValue.trim() || inputValue.length > MAX_QUERY_LENGTH
                    ? undefined
                    : handleSend
                }
              >
                <ArrowUpwardIcon sx={{ fontSize: 18, color: "white" }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {inputValue.length > MAX_QUERY_LENGTH && (
        <Box className="livis-ai-assistant-char-counter-row">
          <Typography
            variant="caption"
            className="livis-ai-assistant-char-counter over-limit"
          >
            {inputValue.length} / {MAX_QUERY_LENGTH}
          </Typography>
          <Typography
            variant="caption"
            className="livis-ai-assistant-char-warning"
          >
            Message too long (max {MAX_QUERY_LENGTH} characters)
          </Typography>
        </Box>
      )}
    </Box>
  )
})
